import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  FormHelperText,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { DateTime } from 'luxon';

import type { ReservationFormInput } from '../schema/reservationForm.schema';

import BooleanRadioButtonGroup from '@/components/forms/BooleanRadioButtonGroup';
import { ReturnDateMismatchMessage } from '../messages/ReturnDateMismatchMessage';
import {
  defaultTimePickerReferenceDate,
  isValidDateTime,
  isWithinReturnDateRange,
  mergeDatePart,
  mergeTimePart,
  safePlus,
  toPickerDateTime,
} from '../utils/pickerDateTime';

const PREFIX = 'RoundTripReturnTripFields';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface RoundTripReturnTripFieldsProps {
  index: number;
}

export function RoundTripReturnTripFields({
  index,
}: RoundTripReturnTripFieldsProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { control, setValue, getValues, trigger } =
    useFormContext<ReservationFormInput>();
  const [timeInteracted, setTimeInteracted] = useState(false);

  const fieldName =
    `trips.${index}.roundTripWillCall` as `trips.${number}.roundTripWillCall`;
  const roundTripWillCall =
    useWatch({
      control,
      name: fieldName,
    }) ?? true;

  const isWillCall =
    useWatch({
      control,
      name: `trips.${index}.isWillCall`,
    }) ?? false;

  const returnFieldName = `trips.${index}.roundTripReturnDateTime` as const;
  const returnDateFieldName = `trips.${index}.roundTripReturnDate` as const;
  const appointmentFieldName = `trips.${index}.appointmentDateTime` as const;

  const appointmentDateTime = useWatch({
    control,
    name: appointmentFieldName,
  }) as DateTime | null | undefined;

  const pairedDateIso = useWatch({
    control,
    name: returnDateFieldName,
  }) as string | undefined;
  const pairedDateValue = toPickerDateTime(
    pairedDateIso ? DateTime.fromISO(pairedDateIso) : null,
  );

  const setPairedDate = (isoDate: string) => {
    setValue(returnDateFieldName, isoDate, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const syncPairedDateString = (next: DateTime | null) => {
    setPairedDate(next?.toISODate() ?? '');
  };

  useEffect(() => {
    if (!isWillCall) return;

    setValue(fieldName, true, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(returnDateFieldName, '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(returnFieldName, null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [fieldName, isWillCall, returnDateFieldName, returnFieldName, setValue]);

  // When toggling will, default return date/time to current appointment value (if set)
  useEffect(() => {
    if (roundTripWillCall) {
      return;
    }

    const currentReturn = getValues(returnFieldName) as
      | DateTime
      | null
      | undefined;
    if (!currentReturn && isValidDateTime(appointmentDateTime)) {
      setValue(returnDateFieldName, appointmentDateTime.toISODate() ?? '', {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(returnFieldName, appointmentDateTime, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [
    roundTripWillCall,
    appointmentDateTime,
    returnDateFieldName,
    returnFieldName,
    getValues,
    setValue,
  ]);

  // Keep return date/time in sync with appointment
  useEffect(() => {
    if (roundTripWillCall) return;
    if (!isValidDateTime(appointmentDateTime)) return;

    setValue(returnDateFieldName, appointmentDateTime.toISODate() ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(returnFieldName, appointmentDateTime, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [
    appointmentDateTime,
    roundTripWillCall,
    returnDateFieldName,
    returnFieldName,
    setValue,
  ]);

  return (
    <StyledBox className={classes.container}>
      <Typography variant="h5" component="h3">
        {t('reservation.returnTrip')}
      </Typography>
      <FormHelperText>
        {t('member.whenYouReadyToPickedUp')}
        <Box component="span" color={theme.palette.error.main}>
          *
        </Box>
      </FormHelperText>
      <FormControl component="fieldset" variant="standard" fullWidth>
        <BooleanRadioButtonGroup
          name={fieldName}
          control={control}
          trueText={t('member.optionCallTextWhenReady')}
          falseText={t('member.optionPickDateTime')}
          disabled={isWillCall}
        />
      </FormControl>
      {!roundTripWillCall && (
        <Box sx={{ mt: 1, ml: 4 }}>
          <Controller
            name={returnFieldName}
            control={control}
            rules={{
              validate: (value?: DateTime | null) => {
                if (roundTripWillCall) return true;

                const hasPairedDate = pairedDateValue != null;
                const hasValidDateTime = isValidDateTime(value);

                if (!hasValidDateTime) {
                  // Return time isn't set yet, but a date may already be —
                  // typing an out-of-range date alone (no time) stores it on
                  // the paired roundTripReturnDate string while this field
                  // stays null. Flag it with the more instructive message;
                  // only nag about the missing value once the time field has
                  // actually been interacted with.
                  if (
                    hasPairedDate &&
                    isValidDateTime(appointmentDateTime) &&
                    !isWithinReturnDateRange(
                      pairedDateValue,
                      appointmentDateTime,
                    )
                  ) {
                    return t('reservation.returnDateInvalidError');
                  }
                  if (!hasPairedDate || timeInteracted) {
                    return t('common.invalidDate');
                  }
                  return true;
                }

                if (
                  isValidDateTime(appointmentDateTime) &&
                  !isWithinReturnDateRange(value, appointmentDateTime)
                ) {
                  return t('reservation.returnDateInvalidError');
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => {
              const fieldDateTime = toPickerDateTime(field.value);
              const effectiveSelectedDate = fieldDateTime ?? pairedDateValue;
              const returnMinDate = isValidDateTime(appointmentDateTime)
                ? appointmentDateTime.startOf('day')
                : undefined;
              const returnMaxDate = isValidDateTime(appointmentDateTime)
                ? safePlus(appointmentDateTime, { days: 1 })?.startOf('day')
                : undefined;
              const helperText = fieldState.error?.message;
              const hasDateOnlySelection =
                pairedDateValue != null && fieldDateTime == null;
              // returnDateBeforePickupError fires on an instant comparison
              // (returnTime < appt), so it also covers a return on the
              // *same* calendar date but an earlier time (e.g. 08:00 vs a
              // 09:00 appointment) — there the date is fine and it's the
              // time that's wrong, so it belongs under Time, not Date.
              const isReturnDateBeforePickupError =
                helperText === t('reservation.returnDateBeforePickupError');
              const isSameDayTimeOnlyViolation =
                isReturnDateBeforePickupError &&
                fieldDateTime != null &&
                isValidDateTime(appointmentDateTime) &&
                fieldDateTime.hasSame(appointmentDateTime, 'day');
              // The remaining out-of-range/before-pickup messages are about
              // the date, not the time — show them under the Date control
              // only, not duplicated under Time too.
              const isReturnDateMismatchError =
                !isSameDayTimeOnlyViolation &&
                (helperText === t('reservation.returnDateInvalidError') ||
                  isReturnDateBeforePickupError);
              const datePickerError =
                !!fieldState.error &&
                !isSameDayTimeOnlyViolation &&
                (!hasDateOnlySelection || isReturnDateMismatchError);
              const timePickerError =
                !!fieldState.error &&
                (isSameDayTimeOnlyViolation || !isReturnDateMismatchError);
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    '& > *': { flex: '1 1 140px', minWidth: 0 },
                  }}>
                  <DatePicker
                    label={t('member.date')}
                    value={fieldDateTime ?? pairedDateValue}
                    minDate={returnMinDate}
                    maxDate={returnMaxDate?.endOf('day')}
                    onChange={(date) => {
                      // The calendar grid still blocks clicking an
                      // out-of-range day (minDate/maxDate above), but a
                      // typed date is let through here on purpose: it needs
                      // to register in form state so the Controller's
                      // rules.validate (above) can flag it with a visible
                      // error — and reservationFormSchema blocks
                      // Next/Submit for the same reason (see
                      // reservationForm.schema.tsx).
                      setTimeInteracted(false);
                      const next = mergeDatePart(fieldDateTime, date);
                      const hasExistingDateTime = fieldDateTime != null;

                      if (hasExistingDateTime) {
                        field.onChange(next);
                        syncPairedDateString(next);
                        void trigger(returnFieldName);
                        return;
                      }

                      // Keep date-only selection without fabricating a
                      // midnight time.
                      field.onChange(null);
                      const selectedDateOnly = toPickerDateTime(date);
                      setPairedDate(selectedDateOnly?.toISODate() ?? '');
                      void trigger(returnFieldName);
                    }}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        required: true,
                        error: datePickerError,
                        helperText: datePickerError ? helperText : null,
                        onBlur: () => field.onBlur(),
                        sx: {
                          width: '100%',
                          '& .MuiInputAdornment-root .MuiIconButton-root': {
                            padding: '4px',
                            marginLeft: '-15px',
                          },
                        },
                      },
                    }}
                  />
                  <DesktopTimePicker
                    label={t('member.time')}
                    value={fieldDateTime}
                    referenceDate={
                      effectiveSelectedDate?.startOf('day') ??
                      defaultTimePickerReferenceDate()
                    }
                    onChange={(time) => {
                      if (time === null) {
                        field.onChange(null);
                        return;
                      }
                      if (!isValidDateTime(time)) return;
                      const fallbackBase =
                        pairedDateValue?.startOf('day') ?? null;
                      const next = mergeTimePart(
                        fieldDateTime ?? fallbackBase,
                        time,
                      );
                      if (next) {
                        field.onChange(next);
                      }
                    }}
                    closeOnSelect={false}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        error: timePickerError,
                        helperText: timePickerError ? helperText : null,
                        onFocus: () => setTimeInteracted(true),
                        onBlur: () => field.onBlur(),
                        sx: { width: '100%' },
                      },
                    }}
                    onOpen={() => setTimeInteracted(true)}
                  />
                </Box>
              );
            }}
          />
          <ReturnDateMismatchMessage index={index} />
        </Box>
      )}
    </StyledBox>
  );
}
